import React, { useContext, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { AppContext } from '@prisjakt/ui-app-context';
import ErrorBoundary from '../error-boundary';
import { getAdService } from './services';
import { getAdContextCookie } from './helpers/cookie';
import { IAdContainerProps, IAdContainerWithVisibilityProps } from './types';
import { AdContainerSkeleton, AdLabel } from './styles';

const IN_VIEW_THRESHOLD = [0];
// A class name that should be blocked by most major ad blockers. Used to save space when an ad blocker is active.
// This particular class name is arbitrarily chosen from https://easylist-downloads.adblockplus.org/easylist.txt
export const adBlockerClassName = 'AdBox';

const AdContainer: React.FC<IAdContainerProps> = props => {
  const { t, market } = useContext(AppContext);
  const serviceName = getAdContextCookie().service || market.ads.service;
  const service = getAdService(serviceName);

  if (
    props.onlyWithServices &&
    Array.isArray(props.onlyWithServices) &&
    !props.onlyWithServices.includes(serviceName)
  ) {
    return null;
  }

  return (
    <AdContainerSkeleton
      $placement={props.placement}
      $spacing={props.spacing}
      className={adBlockerClassName}
    >
      <service.container service={service} {...props}>
        <AdLabel>{t('common', 'ads.label')}</AdLabel>
      </service.container>
    </AdContainerSkeleton>
  );
};

const AdContainerWithVisibility: React.FC<IAdContainerWithVisibilityProps> = props => {
  const [isVisible, setIsVisible] = useState(false);
  const { className, renderWhenInScreen, triggerPoint } = props;
  const { market } = useContext(AppContext);
  const onChangeHandler = (inView: boolean) => {
    if (isVisible !== inView) {
      setIsVisible(inView);
    }
  };

  if (market.ads.service === 'publisherdesk') {
    return (
      <div className={className} data-test="AdContainer">
        <AdContainer {...props} />
      </div>
    );
  }

  return (
    <InView
      threshold={IN_VIEW_THRESHOLD}
      onChange={onChangeHandler}
      rootMargin={`0px 0px ${triggerPoint}px 0px`}
    >
      {({ ref }) => (
        <div ref={ref} className={className} data-test="AdContainer">
          <AdContainer
            {...props}
            isVisible={renderWhenInScreen ? !!isVisible : true}
          />
        </div>
      )}
    </InView>
  );
};

AdContainerWithVisibility.defaultProps = {
  renderWhenInScreen: true,
  className: undefined,
  onStatusChange: () => {},
  triggerPoint: 300,
};

const AdContainerWithErrorBoundary: React.FC<IAdContainerWithVisibilityProps> = props => (
  <ErrorBoundary onError={() => null}>
    <AdContainerWithVisibility {...props} />
  </ErrorBoundary>
);

export default AdContainerWithErrorBoundary;
